/* istanbul ignore file */

import { DynamicMessageInfo } from '../../../../components/LoanPresentation';
import { YourFinanceResponse } from '../yourFinance/yourFinanceTypes';

export type dropdownOptions = {
    value: string,
    key: string,
};

export type loanTermOption = {
    label: string,
    value: string,
}

export type BorrowingPowerSubmitData = {
    bladeName: string,
};

export const DefaultBorrowingPowerSubmitData: BorrowingPowerSubmitData = {
    bladeName: 'borrowing-power',
};

export type BorrowingPowerSubmit = {
    bladeName: string,
};

export const DefaultBorrowingPowerSubmit: BorrowingPowerSubmit = {
    bladeName: 'borrowing-power',
};

export const LoanProduct = {
    amountToolTip: 'Max loan amount {0}',
    heading: '<b>Unsecured loan (variable)</b>',
    repayment: 'If you borrow {0} for {1} years\\n <b>{3}</b> weekly',
    interestComparisonText: '<p>Based on the representative rate of {0}% p.a.^ (Comparison rate 16.83% p.a.)</p>',
    recommendedText: 'Unlimited repayments',
    greenDiscountInfo: `A discounted interest rate is applied for ([eligible sustainable purchases]{Eligible sustainable purchases include electric\
        or hybrid cars, solar panels, battery packs, solar hot water system, heat pump hot water system and electric car charging station(s).})`,
};

export const LoanProductRepayment = {
    heading: 'Estimated repayments',
    description: 'If you borrow {0} for {1}',
    toolTip: 'The total amount you\'ll be borrowing, not including interest or fees from {0} is {1}',
    bottomDescription: 'Based on a representative interest rate of {0}% p.a. ^(Comparison rate {1}% p.a.)',
};

export type LoanProductData = {
    isRecommendedPrimary: boolean,
    isRecommendedSecondary: boolean,
    isRecommendedPrimaryText: string,
    isRecommendedSecondaryText: string,
    isRecommendedOffer: boolean,
    isValid: boolean,
    id: number,
    heading: string,
    cappedMessage: string,
    repayment: {
        heading: string,
        description: string,
        amount: number,
        frequency: string,
        bottomDescription: string,
    },
    activeInterestName: string,
    offer: {
        heading: string,
        description: string[],
    },
    features: {
        heading: string,
        description: string[],
    },
    dynamicMessageInfo: DynamicMessageInfo | undefined,
    params: {
        cappedMessage: string[],
        repaymentHeading: string[],
        repaymentDescription: string[],
        dynamicMessage: string[],
    },
    hyperLinkText: string,
    hyperOpenLink: string,
}

const DefaultBorrowingPowerContent = {
    borrowingCapacityLabel: 'You may be able to borrow up to:',
    borrowingPowerInfo: 'Based on a discounted interest rate of {0}% p.a. (Comparision rate {1}% p.a.)',
    borrowingPowerCappedInfo: 'You can borrow up to {0}(which is the maximum loan amount)',
    heading: 'AAAAAA',
    description: `We've based this estimate on the details you've given us. 
    This may change once you provide more information during the application process.`,
    peronalLoansLink: 'https://www.commbank.com.au/personal-loans.html?ei=anch_pl',
    discoverMore: {
        heading: 'Discover more',
        pods: [
            {
                imgSrc: 'assets/Pod1.jpeg',
                heading: 'Help me choose',
                description: 'Use our personal loan selector tool to choose between Variable, Fixed Rate or Secured.',
                link: `<a target='_blank' id='personal-loan-selector-link-button' href=
                    'https://www.commbank.com.au/digital/tools/personal-loan-selector-tool?ei=tools_selector'>Use selector</a>`,
            },
            {
                imgSrc: 'assets/Pod2.jpeg',
                heading: 'How much does it cost to run an electric vehicle?',
                description: `Use our comparison tool to see the estimated running costs, 
                emissions and vehicle prices of an EV compared to a petrol, diesel or hybrid vehicle.`,
                link: `<a target='_blank' id='ev-calculator-link-button' 
                    href='https://www.commbank.com.au/retail/personal-loans/evcalculator'>Get started</a>`,
            },
            {
                imgSrc: 'assets/Pod3.jpeg',
                heading: 'Repayments calculator',
                description: 'Understand what your repayments will be. Adjust the term and the amount to meet your budget.',
                link: `<a target='_blank' id='tool-repayment-link-button' 
                    href='https://www.commbank.com.au/digital/calculators/personal-loan-repayment-calculator/?ei=tools_repayment'>Use calculator</a>`,
            },
        ],
    },
    normalInterest: '',
    greenInterest: '',
    greenDescription: 'Based on a {0} discount rate of {1}% p.a.³ (Comparison rate {2}% p.a.)',
    interestDescription: '',
    representativeRateInfo: '',
    offerInfo: '',
    greenDiscountInfo: `A discounted interest rate is applied for ([eligible sustainable purchases]{Eligible sustainable purchases include electric\
         or hybrid cars, solar panels, battery packs, solar hot water system, heat pump hot water system and electric car charging station(s).})`,
    dynamicDescription: 'The estimates are limited to your borrowing power {0}.',
    exploreLoanOptionsButtonText: '',
    resetEstimateText: 'Rest estimate tool',
    loanTermsDropDownLabel: 'Estimated repayments over',
    loanOptions: {
        borrowingPowerInfo: 'You can borrow up to {0}',
        borrowingPowerCappedInfo: 'You can borrow up to {0}(which is the maximum loan amount)',
        secured: {
            amountToolTip: "The total amount you'll be borrowing, not including interest or fees.",
            heading: '<b>Secured Personal Loan </b>',
            description: `By using your current car or one you're planning to buy as security, 
            your rate could be lower than an unsecured loan.`,
            evDescription: 'By using the car you’re planning to buy as security, your rate could be lower than an unsecured loan',
            repayment: 'If you borrow {0} for {1} years\\n <b>{3}</b> weekly',
            interestComparisonText: '<p>Based on the representative rate of {0}% p.a.³ (Comparison rate {1}% p.a.)</p>',
            recommendedText: 'Consider',
            greenDiscount: 'Based on a discounted interest rate of {0}% p.a. (Comparison rate {1}% p.a.**)',
            greenDiscountInfo: 'A discounted interest rate is applied',
            borrowingCapacityTooltip: 'The estimates are limited to your borrowing power.',
        },
        variable: {
            amountToolTip: "The total amount you'll be borrowing, not including interest or fees.",
            heading: '<b>Variable Rate Personal Loan</b>',
            description: `You'll pay no fees for making additional repayments, which can help you 
            save interest and pay off your loan sooner. `,
            repayment: 'If you borrow {0} for {1} years\\n <b>{3}</b> weekly',
            interestComparisonText: '<p>Based on the representative rate of {0}% p.a.³ (Comparison rate {1}% p.a.)</p>',
            recommendedText: 'Consider',
            greenDiscount: 'Based on a discounted interest rate of {0}% p.a. (Comparison rate {1}% p.a.**)',
            greenDiscountInfo: 'A discounted interest rate is applied for',
            borrowingCapacityTooltip: 'The estimates are limited to your borrowing power.',
        },
        fixed: {
            amountToolTip: "The total amount you'll be borrowing, not including interest or fees.",
            heading: '<b>Fixed Rate Personal Loan</b>',
            description: `Receive a fixed interest rate over the life of the loan. 
            Know exactly how much your repayments will be.`,
            repayment: 'If you borrow {0} for {1} years\\n <b>{3}</b> weekly',
            interestComparisonText: '<p>Based on the representative rate of {0}% p.a.³ (Comparison rate {1}% p.a.)</p>',
            recommendedText: 'Consider',
            greenDiscount: 'Based on a discounted interest rate of {0}% p.a. (Comparison rate {1}% p.a.**)',
            greenDiscountInfo: 'A discounted interest rate is applied for',
            borrowingCapacityTooltip: 'The estimates are limited to your borrowing power.',
        },
    },
};

export const DefaultRawBorrowingPowerData = {
    data: {
        loanTerms: [
            {
                id: 1,
                years: 1,
                months: 12,
            },
            {
                id: 2,
                years: 2,
                months: 24,
            },
            {
                id: 3,
                years: 3,
                months: 36,
            },
            {
                id: 4,
                years: 4,
                months: 48,
            },
            {
                id: 5,
                years: 5,
                months: 60,
            },
            {
                id: 6,
                years: 6,
                months: 72,
            },
            {
                id: 7,
                years: 7,
                months: 84,
            },
        ],
        loanOptions: [
            {
                id: 83,
                description: 'Variable Rate Personal Loan',
            },
            {
                id: 84,
                description: 'Fixed Rate Personal Loan',
            },
            {
                id: 85,
                description: 'Secured Personal Loan',
            },
        ],
        feesAndCharges: [
            {
                interestRate: 16,
                establishmentFee: 250,
                monthlyServiceFee: 15,
                loanTypeId: 83,
                loanType: 'Standard',
            },
            {
                interestRate: 16,
                establishmentFee: 250,
                monthlyServiceFee: 15,
                loanTypeId: 84,
                loanType: 'Standard',
            },
            {
                interestRate: 8.99,
                establishmentFee: 250,
                monthlyServiceFee: 15,
                loanTypeId: 85,
                loanType: 'Standard',
            },
            {
                interestRate: 7.5,
                establishmentFee: 250,
                monthlyServiceFee: 15,
                loanTypeId: 83,
                loanType: 'Green',
            },
            {
                interestRate: 7.01,
                establishmentFee: 250,
                monthlyServiceFee: 15,
                loanTypeId: 84,
                loanType: 'Green',
            },
            {
                interestRate: 6.49,
                establishmentFee: 250,
                monthlyServiceFee: 15,
                loanTypeId: 85,
                loanType: 'Green',
            },
        ],
        repaymentFrequencies: [
            {
                id: 5,
                description: 'Weekly',
            },
            {
                id: 4,
                description: 'Fortnightly',
            },
            {
                id: 2,
                description: 'Monthly',
            },
        ],
        loanConfiguration: {
            minimumLoanAmount: 4000,
            maximumLoanAmount: 100000,
            minimumUnsecuredLoanAmount: 4000,
            maximumUnsecuredLoanAmount: 50000,
            defaultLoanRepaymentFrequency: 'Weekly',
            defaultLoanTermInYears: 7,
        },
        loanProducts: [
            {
                id: 83,
                content: '',
            },
            {
                id: 84,
                content: '',
            },
            {
                id: 85,
                content: '',
            },
        ],
    },
    content: '',
};

export const DefaultBorrowingPowerData = {
    borrowingCapacityLabel: 'You may be able to borrow up to:',
    borrowingPowerInfo: 'Based on a discounted interest rate of {0}% p.a. (Comparision rate {1}% p.a.)',
    borrowingPowerCappedInfo: 'You can borrow up to {0}(which is the maximum loan amount)',
    heading: 'AAAAAA',
    description: `We've based this estimate on the details you've given us. 
    This may change once you provide more information during the application process.`,
    peronalLoansLink: 'https://www.commbank.com.au/personal-loans.html?ei=anch_pl',
    discoverMore: {
        heading: 'Discover more',
        pods: [
            {
                imgSrc: 'assets/Pod1.jpeg',
                heading: 'Help me choose',
                description: 'Use our personal loan selector tool to choose between Variable, Fixed Rate or Secured.',
                link: `<a target='_blank' id='personal-loan-selector-link-button' href=
                    'https://www.commbank.com.au/digital/tools/personal-loan-selector-tool?ei=tools_selector'>Use selector</a>`,
            },
            {
                imgSrc: 'assets/Pod2.jpeg',
                heading: 'How much does it cost to run an electric vehicle?',
                description: `Use our comparison tool to see the estimated running costs, 
                emissions and vehicle prices of an EV compared to a petrol, diesel or hybrid vehicle.`,
                link: `<a target='_blank' id='ev-calculator-link-button' 
                    href='https://www.commbank.com.au/retail/personal-loans/evcalculator'>Get started</a>`,
            },
            {
                imgSrc: 'assets/Pod3.jpeg',
                heading: 'Repayments calculator',
                description: 'Understand what your repayments will be. Adjust the term and the amount to meet your budget.',
                link: `<a target='_blank' id='tool-repayment-link-button' 
                    href='https://www.commbank.com.au/digital/calculators/personal-loan-repayment-calculator/?ei=tools_repayment'>Use calculator</a>`,
            },
        ],
    },
    normalInterest: '',
    greenInterest: '',
    greenDescription: 'Based on a {0} discount rate of {1}% p.a.³ (Comparison rate {2}% p.a.)',
    interestDescription: '',
    representativeRateInfo: '',
    offerInfo: '',
    greenDiscountInfo: `A discounted interest rate is applied for ([eligible sustainable purchases]{Eligible sustainable purchases include electric\
         or hybrid cars, solar panels, battery packs, solar hot water system, heat pump hot water system and electric car charging station(s).})`,
    dynamicDescription: 'The estimates are limited to your borrowing power {0}.',
    exploreLoanOptionsButtonText: '',
    resetEstimateText: 'Rest estimate tool',
    loanTermsDropDownLabel: 'Estimated repayments over',
    loanOptions: {
        borrowingPowerInfo: 'You can borrow up to {0}',
        borrowingPowerCappedInfo: 'You can borrow up to {0}(which is the maximum loan amount)',
        secured: {
            amountToolTip: "The total amount you'll be borrowing, not including interest or fees.",
            heading: '<b>Secured Personal Loan </b>',
            description: `By using your current car or one you're planning to buy as security, 
            your rate could be lower than an unsecured loan.`,
            repayment: 'If you borrow {0} for {1} years\\n <b>{3}</b> weekly',
            interestComparisonText: '<p>Based on the representative rate of {0}% p.a.³ (Comparison rate {1}% p.a.)</p>',
            recommendedText: 'Consider',
            greenDiscount: 'Based on a discounted interest rate of {0}% p.a. (Comparison rate {1}% p.a.**)',
            greenDiscountInfo: 'A discounted interest rate is applied',
            borrowingCapacityTooltip: 'The estimates are limited to your borrowing power.',
        },
        variable: {
            amountToolTip: "The total amount you'll be borrowing, not including interest or fees.",
            heading: '<b>Variable Rate Personal Loan</b>',
            description: `You'll pay no fees for making additional repayments, which can help you 
            save interest and pay off your loan sooner. `,
            repayment: 'If you borrow {0} for {1} years\\n <b>{3}</b> weekly',
            interestComparisonText: '<p>Based on the representative rate of {0}% p.a.³ (Comparison rate {1}% p.a.)</p>',
            recommendedText: 'Consider',
            greenDiscount: 'Based on a discounted interest rate of {0}% p.a. (Comparison rate {1}% p.a.**)',
            greenDiscountInfo: 'A discounted interest rate is applied for',
            borrowingCapacityTooltip: 'The estimates are limited to your borrowing power.',
        },
        fixed: {
            amountToolTip: "The total amount you'll be borrowing, not including interest or fees.",
            heading: '<b>Fixed Rate Personal Loan</b>',
            description: `Receive a fixed interest rate over the life of the loan. 
            Know exactly how much your repayments will be.`,
            repayment: 'If you borrow {0} for {1} years\\n <b>{3}</b> weekly',
            interestComparisonText: '<p>Based on the representative rate of {0}% p.a.³ (Comparison rate {1}% p.a.)</p>',
            recommendedText: 'Consider',
            greenDiscount: 'Based on a discounted interest rate of {0}% p.a. (Comparison rate {1}% p.a.**)',
            greenDiscountInfo: 'A discounted interest rate is applied for',
            borrowingCapacityTooltip: 'The estimates are limited to your borrowing power.',
        },
    },
    loanYearsForm: {
        DropdownOptions: [
            {
                value: '7',
                key: '7 years',
            },
            {
                value: '6',
                key: '6 years',
            },
            {
                value: '5',
                key: '5 years',
            },
            {
                value: '4',
                key: '4 years',
            },
            {
                value: '3',
                key: '3 years',
            },
            {
                value: '2',
                key: '2 years',
            },
            {
                value: '1',
                key: '1 year',
            },
        ],
    },
};

export type BorrowingPowerContent = typeof DefaultBorrowingPowerContent;
export type BorrowingPowerRawData = typeof DefaultRawBorrowingPowerData;
export type BorrowingPowerData = typeof DefaultBorrowingPowerData;

export interface BorrowingPowerState {
    data: BorrowingPowerData,
    financeData: YourFinanceResponse,
}
